import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  CONFIGURATION_STATUS,
  type ConfigurationStatus,
} from '@/constants/configuration';
import type {
  CondominiumConfigurationResponse,
  CreatedUserInfo,
} from '@/services/api/establishments.service';
import {
  mapConfigurationResponseToStore,
  mapStoreToConfigurationPayload,
} from '@/adapters/condominiumConfiguration';
import type { CondominiumData, MaintenanceSummary } from '@/types/condominium';
import type { MaintenanceSetupItem } from '@/types/maintenance';
import { mapNestErrorToFriendly } from '@/stores/wizard-error-messages';
import * as Sentry from '@sentry/react';

/** Nomes legíveis dos steps do wizard, para envio ao Sentry. */
const WIZARD_STEP_NAMES = [
  'Nome',
  'CNPJ',
  'Tipo',
  'Endereço',
  'Síndico',
  'Subsíndico',
  'Zelador',
  'Administradora',
  'Estrutura',
  'Manutenção',
  'Conclusão',
  'Carregando',
] as const;

interface UserData {
  name: string;
  condominiumData: CondominiumData;
}

interface QuizStore {
  userData: UserData;
  condominiumData: CondominiumData;
  establishmentId?: string;
  configurationStatus: ConfigurationStatus;
  isLoading: boolean;
  isSaving: boolean;
  hasLoadedFromServer: boolean;
  /**
   * True quando o usuário entrou no fluxo para EDITAR um plano já existente.
   * Nesse caso o passo final NÃO deve regerar o plano (o que cancelaria o
   * plano atual e órfãos o histórico) — apenas salva os dados do condomínio.
   */
  isEditingExistingPlan: boolean;
  error?: string;

  /** Usuários criados automaticamente durante a configuração */
  createdUsers: {
    subSyndic?: CreatedUserInfo;
    janitor?: CreatedUserInfo;
  };
  /** Limpar informações dos usuários criados após exibição */
  clearCreatedUsers: () => void;

  currentQuestion: number;
  totalQuestions: number;

  getValue: (path: readonly PathSegment[]) => unknown;
  setUserData: (data: UserData) => void;
  setCondominiumData: (data: CondominiumData) => void;
  updateCondominiumData: (data: Partial<CondominiumData>) => void;
  updateField: (path: readonly PathSegment[], value: unknown) => void;
  updateMaintenanceItems: (
    updater: (items: MaintenanceSetupItem[]) => MaintenanceSetupItem[],
  ) => void;

  setCurrentQuestion: (question: number) => void;
  setEditingExistingPlan: (value: boolean) => void;
  incrementQuestion: () => Promise<void>;
  decrementQuestion: () => void;
  resetQuiz: () => void;

  reset: () => void;
  loadConfiguration: () => Promise<void>;
  savePartial: (options?: { force?: boolean }) => Promise<void>;
  completeConfiguration: () => Promise<void>;
}

type EstablishmentsApi = typeof import('@/services/api/establishments.service')['default'];
let establishmentsApiPromise: Promise<EstablishmentsApi> | null = null;
let configurationSavePromise: Promise<void> | null = null;

async function getEstablishmentsApi() {
  if (!establishmentsApiPromise) {
    establishmentsApiPromise = import('@/services/api/establishments.service').then(
      (module) => module.default,
    );
  }
  return establishmentsApiPromise;
}

type PathSegment = string | number;

type MaintenanceFrequency = 'monthly' | 'quarterly' | 'half_yearly' | 'annual';

const emptySummary: MaintenanceSummary = {
  total: 0,
  enabledCount: 0,
  monthly: 0,
  quarterly: 0,
  half_yearly: 0,
  annual: 0,
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function deepMerge<T extends object>(
  target: T,
  source: Partial<T>,
): T {
  const result = { ...target } as Record<string, unknown>;

  Object.entries(source).forEach(([key, value]) => {
    const targetValue = result[key];

    if (isPlainObject(targetValue) && isPlainObject(value)) {
      result[key] = deepMerge(targetValue, value);
      return;
    }

    result[key] = value;
  });

  return result as T;
}

function setValueAtPath<T>(
  data: T,
  path: readonly PathSegment[],
  value: unknown,
): T {
  const apply = (current: unknown, segments: readonly PathSegment[]): unknown => {
    if (segments.length === 0) {
      return value;
    }

    const [head, ...rest] = segments;

    if (head === undefined) {
      return value;
    }

    if (typeof head === 'number') {
      const currentArray = Array.isArray(current) ? [...current] : [];
      currentArray[head] = apply(currentArray[head], rest);
      return currentArray;
    }

    const currentObject: Record<string, unknown> = isPlainObject(current)
      ? { ...current }
      : {};

    currentObject[head] = apply(currentObject[head], rest);

    return currentObject;
  };

  return apply(data, path) as T;
}

function getValueFromPath(data: unknown, path: readonly PathSegment[]) {
  return path.reduce<unknown>((current, segment) => {
    if (current === null || current === undefined) {
      return undefined;
    }

    if (typeof segment === 'number') {
      if (Array.isArray(current)) {
        return current[segment];
      }
      return undefined;
    }

    if (typeof current === 'object') {
      return (current as Record<string, unknown>)[segment];
    }

    return undefined;
  }, data);
}

function calculateSummary(items: MaintenanceSetupItem[]): MaintenanceSummary {
  const summary: MaintenanceSummary = {
    total: items.length,
    enabledCount: 0,
    monthly: 0,
    quarterly: 0,
    half_yearly: 0,
    annual: 0,
  };

  const projectionYears = 5;

  items.forEach((item) => {
    if (!item.enabled) {
      return;
    }

    summary.enabledCount += 1;

    const frequency = item.customFrequency as MaintenanceFrequency | undefined;

    if (!frequency) {
      return;
    }

    switch (frequency) {
      case 'monthly':
        summary.monthly += 12 * projectionYears;
        break;
      case 'quarterly':
        summary.quarterly += 4 * projectionYears;
        break;
      case 'half_yearly':
        summary.half_yearly += 2 * projectionYears;
        break;
      case 'annual':
        summary.annual += 1 * projectionYears;
        break;
    }
  });

  summary.total =
    summary.monthly + summary.quarterly + summary.half_yearly + summary.annual;

  return summary;
}

function getDefaultMaintenanceSetup() {
  return {
    startDate: undefined,
    items: [] as MaintenanceSetupItem[],
    summary: { ...emptySummary },
  };
}

function getDefaultCondominiumData(): CondominiumData {
  return {
    name: '',
    document: '',
    type: 'residential',
    address: '',
    cep: '',
    neighborhood: '',
    city: '',
    state: '',
    contactPhone: '',
    contactEmail: '',
    website: '',
    securityCompany: { exists: false },
    cleaningCompany: { exists: false },
    erpSystem: { exists: false },
    elevatorCompany: {
      hasElevator: false,
      exists: false,
      elevators: [],
    },
    maintenanceSetup: getDefaultMaintenanceSetup(),
  };
}

function hydrateCondominiumData(
  partial?: Partial<CondominiumData>,
): CondominiumData {
  const defaults = getDefaultCondominiumData();
  const merged = {
    ...defaults,
    ...partial,
  };

  merged.maintenanceSetup = partial?.maintenanceSetup
    ? {
        startDate: partial.maintenanceSetup.startDate,
        items: partial.maintenanceSetup.items ?? [],
        summary: partial.maintenanceSetup.summary ?? { ...emptySummary },
      }
    : getDefaultMaintenanceSetup();

  return merged;
}

/**
 * Extrai o erro do AxiosError/Error e devolve `{ friendly, technical, status, field }`.
 *
 * Importante: AxiosError extends Error — checamos `response.data` ANTES de
 * cair no fallback `Error.message` para não perder a mensagem do backend.
 */
export function extractWizardError(error: unknown): {
  friendly: string;
  technical: string[];
  status?: number;
  field?: string;
} {
  let rawMessage:
    | string
    | string[]
    | Record<string, unknown>
    | undefined = undefined;
  let status: number | undefined;

  if (typeof error === 'object' && error !== null && 'response' in error) {
    const errorWithResponse = error as {
      response?: { status?: number; data?: unknown };
    };
    if (isPlainObject(errorWithResponse.response)) {
      const response = errorWithResponse.response as Record<string, unknown>;
      if (typeof response['status'] === 'number') {
        status = response['status'];
      }
      const data = response['data'];
      if (typeof data === 'string') {
        rawMessage = data;
      } else if (Array.isArray(data)) {
        rawMessage = data as string[];
      } else if (isPlainObject(data)) {
        const message = (data as Record<string, unknown>)['message'];
        if (typeof message === 'string' || Array.isArray(message)) {
          rawMessage = message as string | string[];
        } else if (message != null) {
          rawMessage = data as Record<string, unknown>;
        } else {
          rawMessage = data as Record<string, unknown>;
        }
      }
    }
  }

  if (rawMessage === undefined && error instanceof Error) {
    rawMessage = error.message;
  }

  const { friendly, technical, field } = mapNestErrorToFriendly(
    rawMessage,
    status,
  );

  return { friendly, technical, status, field };
}

const createMemoryStorage = (): Storage => {
  const store = new Map<string, string>();
  return {
    get length() {
      return store.size;
    },
    clear: () => {
      store.clear();
    },
    getItem: (key) => store.get(key) ?? null,
    key: (index) => Array.from(store.keys())[index] ?? null,
    removeItem: (key) => {
      store.delete(key);
    },
    setItem: (key, value) => {
      store.set(key, value);
    },
  };
};

const memoryStorage = createMemoryStorage();

const safeStateStorage = {
  getItem: (key: string) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return memoryStorage.getItem(key);
    }
  },
  setItem: (key: string, value: string) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      memoryStorage.setItem(key, value);
    }
  },
  removeItem: (key: string) => {
    try {
      localStorage.removeItem(key);
    } catch {
      memoryStorage.removeItem(key);
    }
  },
};

const storage = createJSONStorage(() => safeStateStorage) ?? createJSONStorage(createMemoryStorage);

const createBaseState = () => {
  const defaultCondominiumData = getDefaultCondominiumData();
  return {
    userData: { name: '', condominiumData: defaultCondominiumData },
    condominiumData: defaultCondominiumData,
    establishmentId: undefined as string | undefined,
    configurationStatus: CONFIGURATION_STATUS.NOT_STARTED as ConfigurationStatus,
    isLoading: false,
    isSaving: false,
    hasLoadedFromServer: false,
    isEditingExistingPlan: false,
    error: undefined as string | undefined,
    createdUsers: {} as { subSyndic?: CreatedUserInfo; janitor?: CreatedUserInfo },
    currentQuestion: 0,
    totalQuestions: 11,
  };
};

export const useQuizStore = create<QuizStore>()(
  persist(
    (set, get) => ({
      ...createBaseState(),

      getValue: (path) => getValueFromPath(get().condominiumData, path),

      setUserData: (data) => {
        set(() => ({
          userData: data,
          condominiumData: data.condominiumData,
        }));
      },

      setCondominiumData: (data) => {
        set((state) => ({
          condominiumData: data,
          userData: { ...state.userData, condominiumData: data },
        }));
      },

      updateCondominiumData: (data) => {
        set((state) => {
          const merged = deepMerge(state.condominiumData, data);
          return {
            condominiumData: merged,
            userData: { ...state.userData, condominiumData: merged },
          };
        });
      },

      updateField: (path, value) => {
        set((state) => {
          const updated = setValueAtPath(state.condominiumData, path, value);
          return {
            condominiumData: updated,
            userData: { ...state.userData, condominiumData: updated },
          };
        });
      },

      updateMaintenanceItems: (updater) => {
        set((state) => {
          const currentSetup =
            state.condominiumData.maintenanceSetup ?? getDefaultMaintenanceSetup();

          const nextItems = updater([...currentSetup.items]);
          const summary = calculateSummary(nextItems);

          console.log('[updateMaintenanceItems] ✓ Atualizando items:',
            currentSetup.items.length, '→', nextItems.length);

          const nextSetup = {
            ...currentSetup,
            items: nextItems,
            summary,
          };

          const nextData = {
            ...state.condominiumData,
            maintenanceSetup: nextSetup,
          };

          return {
            condominiumData: nextData,
            userData: { ...state.userData, condominiumData: nextData },
          };
        });
      },

      setCurrentQuestion: (question) =>
        set({ currentQuestion: question, error: undefined }),

      setEditingExistingPlan: (value) =>
        set({ isEditingExistingPlan: value }),

      clearCreatedUsers: () => set({ createdUsers: {} }),

      incrementQuestion: async () => {
        try {
          // IMPORTANTE: Usar force: true para garantir que salvamos o estado ATUAL
          // e não reutilizamos uma promise antiga que pode ter dados desatualizados
          await get().savePartial({ force: true });
          // Só avança se o save foi bem sucedido
          set((state) => ({
            currentQuestion: Math.min(
              state.currentQuestion + 1,
              state.totalQuestions,
            ),
            // Limpar erro anterior ao avançar com sucesso
            error: undefined,
          }));
        } catch (error) {
          console.error('[Quiz] Erro ao salvar antes de avançar:', error);

          const { friendly, technical, status, field } =
            extractWizardError(error);

          // Mostrar toast e fixar a mensagem inline no step (lido por QuizContent)
          set({ error: friendly });
          const { showErrorToast } = await import('@/utils/toast');
          showErrorToast(friendly, { duration: 5000 });

          // Enriquecer o evento no Sentry com contexto do wizard antes de re-throw.
          // O onunhandledrejection do Sentry vai capturar via captureException da withScope.
          try {
            const state = get();
            Sentry.withScope((scope) => {
              scope.setTag('wizard_step', String(state.currentQuestion));
              scope.setTag(
                'wizard_step_name',
                WIZARD_STEP_NAMES[state.currentQuestion] ?? 'unknown',
              );
              if (state.establishmentId) {
                scope.setTag('establishment_id', state.establishmentId);
              }
              scope.setTag('http_status', String(status ?? 'unknown'));
              if (field) scope.setTag('error_field', field);
              scope.setContext('wizard', {
                currentQuestion: state.currentQuestion,
                hasLoadedFromServer: state.hasLoadedFromServer,
                establishmentId: state.establishmentId,
                configurationStatus: state.configurationStatus,
                populatedTopKeys: Object.entries(
                  state.condominiumData ?? {},
                )
                  .filter(([, v]) => v != null && v !== '')
                  .map(([k]) => k),
              });
              scope.setContext('error_details', {
                technical,
                friendly,
                status,
                field,
                backend_message: (
                  error as { response?: { data?: unknown } } | undefined
                )?.response?.data,
              });
              Sentry.captureException(error);
            });
          } catch (sentryError) {
            // Não falhar o fluxo do usuário por erro de instrumentação
            console.warn('[Quiz] Falha ao reportar contexto ao Sentry:', sentryError);
          }

          // Re-throw para componentes que queiram tratar
          throw error;
        }
      },

      decrementQuestion: () =>
        set((state) => ({
          currentQuestion: Math.max(state.currentQuestion - 1, 0),
          error: undefined,
        })),

      resetQuiz: () => {
        set({ currentQuestion: 0 });
      },

      reset: () => {
        set(() => {
          const defaultData = getDefaultCondominiumData();
          return {
            condominiumData: defaultData,
            userData: { name: '', condominiumData: defaultData },
            currentQuestion: 0,
            configurationStatus: CONFIGURATION_STATUS.NOT_STARTED,
            isEditingExistingPlan: false,
          };
        });
      },

      loadConfiguration: async () => {
        // ═══════════════════════════════════════════════════════════════
        // CRÍTICO: Capturar estado LOCAL ANTES de fazer a requisição
        // Isso permite preservar items locais se a API retornar items vazios
        // ═══════════════════════════════════════════════════════════════
        const localStateBefore = get();
        const localItems = localStateBefore.condominiumData.maintenanceSetup?.items ?? [];

        console.log('[loadConfiguration] ▶▶▶ INÍCIO');
        console.log('[loadConfiguration] - Items locais ANTES da requisição:', localItems.length);

        set({ isLoading: true, error: undefined });
        try {
          const api = await getEstablishmentsApi();
          const response = await api.getCurrentConfiguration();
          if (!response) {
            const defaultData = getDefaultCondominiumData();

            // IMPORTANTE: Preservar items locais APENAS se forem do mesmo
            // estabelecimento (caso contrário, são lixo de outra sessão).
            // Quando a API retorna null, não temos id pra comparar; só preservamos
            // se o estado local também não estiver associado a algum establishment.
            if (
              localItems.length > 0 &&
              localStateBefore.establishmentId === undefined
            ) {
              console.log('[loadConfiguration] ⚠️ API retornou null, mas temos items locais. Preservando:', localItems.length);
              defaultData.maintenanceSetup = localStateBefore.condominiumData.maintenanceSetup;
            }

            set({
              condominiumData: defaultData,
              userData: { name: '', condominiumData: defaultData },
              establishmentId: undefined,
              configurationStatus: CONFIGURATION_STATUS.NOT_STARTED,
              isLoading: false,
              hasLoadedFromServer: true,
            });
            return;
          }

          // DEBUG: Log do que vem da API
          console.log('[loadConfiguration] - response.id:', response.id);
          console.log('[loadConfiguration] - response.maintenanceSetup:', {
            exists: !!response.maintenanceSetup,
            itemsCount: response.maintenanceSetup?.items?.length ?? 0,
          });

          const responseData = mapConfigurationResponseToStore(response);

          // ═══════════════════════════════════════════════════════════════
          // CRÍTICO: Preservar items LOCAIS se a API retornar items vazios
          // O estado local é a fonte de verdade durante o wizard.
          // GUARD: só preservar se o estado local pertencer ao MESMO
          // estabelecimento (caso contrário, é contaminação de outra sessão).
          // ═══════════════════════════════════════════════════════════════
          const apiItems = responseData.maintenanceSetup?.items ?? [];
          const canPreserveLocal =
            localStateBefore.establishmentId === response.id;
          if (
            canPreserveLocal &&
            localItems.length > 0 &&
            apiItems.length === 0
          ) {
            console.log('[loadConfiguration] ⚠️ API retornou 0 items, mas temos', localItems.length, 'items locais. PRESERVANDO!');
            responseData.maintenanceSetup = localStateBefore.condominiumData.maintenanceSetup;
          } else if (
            !canPreserveLocal &&
            localItems.length > 0 &&
            apiItems.length === 0
          ) {
            console.log('[loadConfiguration] ⚠️ Items locais pertencem a OUTRO estabelecimento, descartando.');
          }

          const nextData = hydrateCondominiumData(responseData);

          console.log('[loadConfiguration] - Após processamento, nextData.maintenanceSetup.items.length:',
            nextData.maintenanceSetup?.items?.length ?? 0);

          set((state) => ({
            condominiumData: nextData,
            userData: { ...state.userData, condominiumData: nextData },
            establishmentId: response.id,
            configurationStatus: response.configurationStatus,
            isLoading: false,
            hasLoadedFromServer: true,
          }));

          // Note: We intentionally do NOT set localStorage here.
          // Establishment selection is managed by EstablishmentContext.
          // Writing here would overwrite user's establishment selection.
        } catch (error) {
          set({
            error: extractWizardError(error).friendly,
            isLoading: false,
            hasLoadedFromServer: true,
          });
        }
      },

      savePartial: async (options) => {
        // ═══════════════════════════════════════════════════════════════
        // CRÍTICO: Capturar snapshot IMEDIATAMENTE, antes de qualquer await
        // Isso garante que temos os dados do usuário no momento do clique,
        // mesmo que uma promise anterior esteja ativa e sobrescreva o estado.
        //
        // GENERATION TOKEN: capturamos o establishmentId atual e validamos no
        // momento de aplicar a resposta. Se o usuário trocou de condomínio
        // durante o save, descartamos o resultado para evitar sobrescrever
        // dados do novo condomínio com a resposta do anterior.
        // ═══════════════════════════════════════════════════════════════
        const snapshotBeforeAwait = get();
        const startEstablishmentId = snapshotBeforeAwait.establishmentId;

        // DEBUG: Log no início, antes de qualquer await
        console.log('[savePartial] ▶▶▶ INÍCIO - Estado capturado IMEDIATAMENTE:');
        console.log('[savePartial] - force:', options?.force);
        console.log('[savePartial] - hasLoadedFromServer:', snapshotBeforeAwait.hasLoadedFromServer);
        console.log('[savePartial] - establishmentId:', snapshotBeforeAwait.establishmentId);
        console.log('[savePartial] - maintenanceSetup.items.length:',
          snapshotBeforeAwait.condominiumData.maintenanceSetup?.items?.length ?? 0);
        console.log('[savePartial] - configurationSavePromise ativo:', !!configurationSavePromise);

        if (configurationSavePromise) {
          if (!options?.force) {
            return configurationSavePromise;
          }
          await configurationSavePromise;
        }

        // Usar o snapshot capturado ANTES de qualquer await
        const state = snapshotBeforeAwait;
        if (!state.hasLoadedFromServer) {
          console.log('[savePartial] ⚠️ EARLY RETURN: hasLoadedFromServer é false - NÃO VAI SALVAR!');
          return;
        }

        if (!state.condominiumData.name?.trim() || !state.condominiumData.document) {
          console.log('[savePartial] ⚠️ EARLY RETURN: name ou document vazios - NÃO VAI SALVAR!');
          console.log('[savePartial] - name:', state.condominiumData.name);
          console.log('[savePartial] - document:', state.condominiumData.document);
          return;
        }

        console.log('[savePartial] ✓ Passou nas validações, vai prosseguir com o save');

        set({ isSaving: true, error: undefined });

        const saveOperation = (async () => {
          try {
            // ═══════════════════════════════════════════════════════════════
            // PROTEÇÃO EXTRA: Comparar snapshot com estado atual e usar o melhor
            // Isso protege contra race conditions onde o estado mudou após o snapshot
            // ═══════════════════════════════════════════════════════════════
            const currentState = get();
            const snapshotItems = snapshotBeforeAwait.condominiumData.maintenanceSetup?.items ?? [];
            const currentItems = currentState.condominiumData.maintenanceSetup?.items ?? [];

            // Usar o estado que tiver MAIS items (proteção contra sobrescrita)
            const bestState = currentItems.length > snapshotItems.length ? currentState : snapshotBeforeAwait;
            const snapshot = bestState;

            // DEBUG: Log detalhado para identificar onde os items estão sendo perdidos
            console.log('[savePartial] ═══════════════════════════════════════');
            console.log('[savePartial] 1. snapshotBeforeAwait.items:', snapshotItems.length);
            console.log('[savePartial] 2. currentState.items:', currentItems.length);
            console.log('[savePartial] 3. Usando estado com', snapshot.condominiumData.maintenanceSetup?.items?.length ?? 0, 'items');
            console.log('[savePartial] 4. Primeiros 3 items:', snapshot.condominiumData.maintenanceSetup?.items?.slice(0, 3).map(i => ({ type: i.type, enabled: i.enabled })));

            const payload = mapStoreToConfigurationPayload(snapshot.condominiumData);

            console.log('[savePartial] 2. payload.maintenanceSetup:', {
              exists: !!payload.maintenanceSetup,
              itemsCount: payload.maintenanceSetup?.items?.length ?? 0,
              items: payload.maintenanceSetup?.items?.slice(0, 3).map(i => ({ type: i.type, enabled: i.enabled })),
            });
            console.log('[savePartial] 3. Full payload keys:', Object.keys(payload));
            console.log('[savePartial] ═══════════════════════════════════════');

            const api = await getEstablishmentsApi();

            console.log('[savePartial] 4. Chamando API:', snapshot.establishmentId ? 'updateConfiguration' : 'createConfiguration');
            console.log('[savePartial] 5. Payload maintenanceSetup.items enviado:', payload.maintenanceSetup?.items?.length ?? 0);

            // updateConfiguration retorna { establishment, subSyndicUser?, janitorUser? }
            // createConfiguration retorna CondominiumConfigurationResponse diretamente
            let response: CondominiumConfigurationResponse;
            let createdUsers: { subSyndic?: CreatedUserInfo; janitor?: CreatedUserInfo } = {};

            if (snapshot.establishmentId) {
              const updateResult = await api.updateConfiguration(snapshot.establishmentId, payload);
              response = updateResult.establishment;
              createdUsers = {
                subSyndic: updateResult.subSyndicUser,
                janitor: updateResult.janitorUser,
              };
            } else {
              response = await api.createConfiguration(payload);
            }

            console.log('[savePartial] 6. Resposta da API recebida:');
            console.log('[savePartial] - response.id:', response.id);
            console.log('[savePartial] - response.maintenanceSetup?.items?.length:', response.maintenanceSetup?.items?.length ?? 0);
            if (createdUsers.subSyndic || createdUsers.janitor) {
              console.log('[savePartial] - createdUsers:', {
                subSyndic: createdUsers.subSyndic ? { name: createdUsers.subSyndic.name, isNew: createdUsers.subSyndic.isNew } : undefined,
                janitor: createdUsers.janitor ? { name: createdUsers.janitor.name, isNew: createdUsers.janitor.isNew } : undefined,
              });
            }

            // GENERATION TOKEN: se o usuário trocou de condomínio durante o save,
            // descartar resultado pra não sobrescrever estado do novo condomínio.
            // - `startEstablishmentId === undefined` (criação) é OK porque a
            //   resposta traz o novo id, e não há condomínio "anterior" pra
            //   colidir.
            // - Em update, exigimos que o establishmentId continue o mesmo.
            const currentEstablishmentId = get().establishmentId;
            if (
              startEstablishmentId !== undefined &&
              currentEstablishmentId !== startEstablishmentId
            ) {
              console.warn(
                '[savePartial] Descartando resposta: condomínio mudou durante o save',
                { startEstablishmentId, currentEstablishmentId },
              );
              return;
            }

            // ═══════════════════════════════════════════════════════════════
            // CRÍTICO: Preservar o maintenanceSetup com MAIS items
            // Comparar: snapshot usado, estado atual, e resposta da API
            // Usar o que tiver mais items para evitar perda de dados
            // ═══════════════════════════════════════════════════════════════
            const finalCurrentState = get();
            const snapshotSetup = snapshot.condominiumData.maintenanceSetup;
            const currentSetup = finalCurrentState.condominiumData.maintenanceSetup;

            const snapshotItemCount = snapshotSetup?.items?.length ?? 0;
            const currentItemCount = currentSetup?.items?.length ?? 0;

            // Escolher o maintenanceSetup com mais items
            const bestMaintenanceSetup = currentItemCount > snapshotItemCount ? currentSetup : snapshotSetup;

            console.log('[savePartial] 7. Preservando maintenanceSetup:');
            console.log('[savePartial] - snapshot items:', snapshotItemCount);
            console.log('[savePartial] - current items:', currentItemCount);
            console.log('[savePartial] - usando:', bestMaintenanceSetup?.items?.length ?? 0, 'items');

            const responseData = mapConfigurationResponseToStore(response);

            if (bestMaintenanceSetup) {
              responseData.maintenanceSetup = bestMaintenanceSetup;
            }

            const nextData = hydrateCondominiumData(responseData);

            // Filtrar apenas usuários que foram realmente criados (com senha temporária)
            const newCreatedUsers: { subSyndic?: CreatedUserInfo; janitor?: CreatedUserInfo } = {};
            if (createdUsers.subSyndic?.isNew && createdUsers.subSyndic?.tempPassword) {
              newCreatedUsers.subSyndic = createdUsers.subSyndic;
            }
            if (createdUsers.janitor?.isNew && createdUsers.janitor?.tempPassword) {
              newCreatedUsers.janitor = createdUsers.janitor;
            }

            set((currentState) => ({
              condominiumData: nextData,
              userData: { ...currentState.userData, condominiumData: nextData },
              establishmentId: response.id,
              configurationStatus: response.configurationStatus,
              // Accumulate created users across autosaves. Subsequent saves
              // return empty (isNew=false) once a user already exists, so a
              // plain overwrite would drop the captured temp passwords before
              // the user reaches the finish step where the modal is shown.
              createdUsers: {
                ...currentState.createdUsers,
                ...newCreatedUsers,
              },
            }));

            // Note: We intentionally do NOT set localStorage here.
            // Establishment selection is managed by EstablishmentContext.
            // Writing here would overwrite user's establishment selection.
          } catch (error) {
            set({ error: extractWizardError(error).friendly });
            throw error;
          } finally {
            set({ isSaving: false });
          }
        })();

        configurationSavePromise = saveOperation;
        try {
          await saveOperation;
        } finally {
          if (configurationSavePromise === saveOperation) {
            configurationSavePromise = null;
          }
        }
      },

      completeConfiguration: async () => {
        console.log('[completeConfiguration] ▶▶▶ INÍCIO');
        const state = get();
        console.log('[completeConfiguration] - Estado antes do savePartial:');
        console.log('[completeConfiguration] - establishmentId:', state.establishmentId);
        console.log('[completeConfiguration] - maintenanceSetup.items.length:',
          state.condominiumData.maintenanceSetup?.items?.length ?? 0);

        // IMPORTANTE: Usar force: true para garantir que salvamos o estado ATUAL
        await state.savePartial({ force: true });

        const refreshedState = get();
        const establishmentId = refreshedState.establishmentId;

        console.log('[completeConfiguration] - Estado DEPOIS do savePartial:');
        console.log('[completeConfiguration] - establishmentId:', refreshedState.establishmentId);
        console.log('[completeConfiguration] - maintenanceSetup.items.length:',
          refreshedState.condominiumData.maintenanceSetup?.items?.length ?? 0);

        if (!establishmentId) {
          throw new Error(
            'Não foi possível identificar o condomínio para finalizar a configuração.',
          );
        }

        const maintenanceSetup = refreshedState.condominiumData.maintenanceSetup;
        if (!maintenanceSetup || maintenanceSetup.items.length === 0) {
          console.log('[completeConfiguration] ⚠️ ERRO: items vazios no estado local');
          throw new Error('Selecione ao menos um item de manutenção.');
        }

        console.log('[completeConfiguration] ✓ Validação passou, vai chamar API complete-configuration');

        // startDate is now optional - maintenances will be created with scheduledDate = null
        // for manual scheduling later

        set({ isSaving: true, error: undefined });
        try {
          const api = await getEstablishmentsApi();
          await api.completeConfiguration(establishmentId);
          set({
            configurationStatus: CONFIGURATION_STATUS.COMPLETED,
            isSaving: false,
          });
        } catch (error) {
          set({
            error: extractWizardError(error).friendly,
            isSaving: false,
          });
          throw error;
        }
      },
    }),
    {
      name: 'condominium-quiz-storage',
      version: 1,
      storage,
      partialize: (state) => ({
        userData: state.userData,
        condominiumData: state.condominiumData,
        establishmentId: state.establishmentId,
        currentQuestion: state.currentQuestion,
        configurationStatus: state.configurationStatus,
      }),
    },
  ),
);

export const resetQuizStoreState = () => {
  const base = createBaseState();
  try {
    useQuizStore.persist?.clearStorage?.();
  } catch (error) {
    console.warn('Não foi possível limpar o armazenamento do quiz', error);
  }
  useQuizStore.setState((state) => ({
    ...state,
    ...base,
  }));
};

export type { CondominiumData, MaintenanceSummary } from '@/types/condominium';
